import { logger } from '@/lib';
import { MEMBERSHIP_STATUS_CONFIG } from '@/lib';
import {
  MEMBERSHIP_DISPLAY_STATUS,
  type MembershipDateRange,
} from './constants';
import {
  getMembershipStatus,
  isMembershipActive,
  isMembershipUpcoming,
} from './status';

/**
 * Relevance priority (lower = higher priority).
 * Active > Upcoming > Expired > Cancelled.
 */
export const getMembershipPriority = (
  membership: MembershipDateRange,
  referenceDate?: Date
): number => {
  switch (getMembershipStatus(membership, referenceDate)) {
    case MEMBERSHIP_DISPLAY_STATUS.ACTIVE: return 1;
    case MEMBERSHIP_DISPLAY_STATUS.UPCOMING: return 2;
    case MEMBERSHIP_DISPLAY_STATUS.EXPIRED: return 3;
    case MEMBERSHIP_DISPLAY_STATUS.CANCELLED: return 4;
    default: return 5;
  }
};

/**
 * Single sortable weight combining priority + date recency.
 * Lower weight = higher relevance.
 */
export const getMembershipSortWeight = (
  membership: MembershipDateRange,
  referenceDate?: Date
): number => {
  if (!membership.start_date || !membership.end_date) {
    return MEMBERSHIP_STATUS_CONFIG.WORST_WEIGHT;
  }

  try {
    const startDate = new Date(membership.start_date);
    const endDate = new Date(membership.end_date);

    if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
      return MEMBERSHIP_STATUS_CONFIG.WORST_WEIGHT;
    }

    const priority = getMembershipPriority(membership, referenceDate);
    const startTime = startDate.getTime();
    const baseWeight = priority * MEMBERSHIP_STATUS_CONFIG.PRIORITY_MULTIPLIER;
    const maxTimestamp = new Date(`${MEMBERSHIP_STATUS_CONFIG.MAX_FUTURE_YEAR}-12-31`).getTime();

    switch (priority) {
      case 1: // Active: earlier start = lower weight
      case 2: // Upcoming: earlier start = lower weight
        return baseWeight + (startTime / MEMBERSHIP_STATUS_CONFIG.TIMESTAMP_DIVISOR);
      case 3: // Expired: more recent start = lower weight (inverted)
        return baseWeight + ((maxTimestamp - startTime) / MEMBERSHIP_STATUS_CONFIG.TIMESTAMP_DIVISOR);
      case 4: { // Cancelled: most recently cancelled = lower weight
        const cancelledAt = membership.cancelled_at ? new Date(membership.cancelled_at).getTime() : startTime;
        return baseWeight + ((maxTimestamp - cancelledAt) / MEMBERSHIP_STATUS_CONFIG.TIMESTAMP_DIVISOR);
      }
      default:
        return baseWeight + (startTime / MEMBERSHIP_STATUS_CONFIG.TIMESTAMP_DIVISOR);
    }
  } catch (error) {
    logger.error('Error calculating membership sort weight', error);
    return MEMBERSHIP_STATUS_CONFIG.WORST_WEIGHT;
  }
};

export const getActiveMemberships = <T extends MembershipDateRange>(
  memberships: T[],
  referenceDate?: Date
): T[] => {
  if (!memberships || memberships.length === 0) return [];
  return memberships.filter(m => isMembershipActive(m, referenceDate));
};

/**
 * Pick the most relevant membership from a list.
 * Priority: Active > Upcoming > Most recent expired > Most recent cancelled.
 */
export const selectMostRelevantMembership = <T extends MembershipDateRange>(
  memberships: T[],
  referenceDate?: Date
): T | null => {
  if (!memberships || memberships.length === 0) return null;

  const sorted = memberships
    .map(membership => ({
      membership,
      weight: getMembershipSortWeight(membership, referenceDate),
    }))
    .sort((a, b) => {
      if (a.weight < b.weight) return -1;
      if (a.weight > b.weight) return 1;
      return 0;
    });

  return sorted[0]?.membership || null;
};

export interface DisplayMembershipsResult<T> {
  /** Memberships to render as badges, in display order (active first, then upcoming by soonest start). */
  memberships: T[];
  /** True when nothing is active or upcoming and a single "most relevant" membership was chosen as fallback. */
  isFallback: boolean;
}

/**
 * The single source of truth for "which memberships should this UI surface show for a member?"
 *
 * Rule (identical on every surface):
 *   1. Show all currently-active memberships, followed by all upcoming memberships
 *      (upcoming sorted by soonest start first).
 *   2. If there are none, fall back to a single `selectMostRelevantMembership`
 *      (most-recent expired, else most-recently-cancelled). Returns `[]` if the member has no
 *      memberships at all — callers typically render a "No Membership" badge in that case.
 */
export const getMembershipsToDisplay = <T extends MembershipDateRange>(
  memberships: T[] | undefined | null,
  referenceDate?: Date
): DisplayMembershipsResult<T> => {
  const all = memberships ?? [];
  const active = getActiveMemberships(all, referenceDate);
  const upcoming = all
    .filter(m => isMembershipUpcoming(m, referenceDate))
    .sort((a, b) => {
      const aStart = a.start_date ? new Date(a.start_date).getTime() : Infinity;
      const bStart = b.start_date ? new Date(b.start_date).getTime() : Infinity;
      return aStart - bStart;
    });

  const primary = [...active, ...upcoming];
  if (primary.length > 0) {
    return { memberships: primary, isFallback: false };
  }

  const fallback = selectMostRelevantMembership(all, referenceDate);
  return { memberships: fallback ? [fallback] : [], isFallback: true };
};
